import { devlog } from '../../../logic/utils.ts';
import { AsTextButton } from './as-text-button.ts';

/**
 * # AsMeshExportButton Component
 *
 * A drop-in replacement for as-text-button that handles mesh export operations
 * with proper file picker integration while preserving user activation context.
 *
 * ## Features
 * - Drop-in compatible with as-text-button
 * - Handles file picker directly to preserve user activation when name="export-mesh"
 * - Supports manual mode for custom processing workflows
 * - Falls back to regular button behavior for other button names
 *
 * ## Attributes
 * - All as-text-button attributes are supported
 * - `manual-mode`: When present, only shows file picker and dispatches event for manual processing
 * - `file-type`: File type to export (default: 'stl')
 * - `suggested-name`: Suggested filename (default: 'mesh')
 * - `fs-api`: When present, uses File System Access API (writes directly to selected file)
 *
 * ## Usage
 * Simply replace `<as-text-button name="export-mesh">Export</as-text-button>`
 * with `<as-mesh-export-button>Export</as-mesh-export-button>`
 *
 * The name attribute is automatically set to 'export-mesh'
 */

export class AsMeshExportButton extends AsTextButton {
  static get observedAttributes() {
    return [...AsTextButton.observedAttributes, 'manual-mode', 'file-type', 'suggested-name', 'fs-api'];
  }

  constructor() {
    super();
    // Always set name to 'export-mesh'
    this.setAttribute('name', 'export-mesh');
  }

  /**
   * Override the event listener setup to intercept export-mesh clicks
   */
  protected setupEventListeners() {
    const button = this.shadowRoot!.querySelector('button');

    button?.addEventListener('click', async (e) => {
      // 1. Always dispatch the button click for logging
      this.dispatchEvent(new CustomEvent('as-button-click', {
        bubbles: true,
        composed: true,
        detail: { buttonName: 'export-mesh' }
      }));

      // 2. Handle file chooser first, then ask as-app for mesh
      try {
        await this.handleMeshExport();
      } catch (error) {
        // If user cancels or there's an error, that's it - logging already happened
       devlog('Mesh export cancelled or failed:', error.message);
      }
    });
  }

  /**
   * Handle the mesh export process
   * 3. Enable file chooser first to avoid user activation errors
   * 4. Get mesh URL from configurator and handle download
   */
  private async handleMeshExport(): Promise<void> {
    const fileType = this.getAttribute('file-type') || 'stl';
    const suggestedName = this.getAttribute('suggested-name') || 'mesh';
    const useFsApi = this.hasAttribute('fs-api');
    const filename = `${suggestedName}.${fileType}`;

    // 4. Get mesh URL from configurator
    const meshURL = await this.getMeshURL(fileType, filename);
    const blob = await this.fetchMeshBlob(meshURL);

    if (useFsApi && 'showSaveFilePicker' in window) {
      // File System Access API: write directly to selected file
      const fileHandle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [{
          description: `${fileType.toUpperCase()} files`,
          accept: { [`application/${fileType}`]: [`.${fileType}`] }
        }]
      });

      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();

      devlog('Mesh saved successfully to:', fileHandle.name);

    } else {
      // Default: blob download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      devlog('Mesh downloaded as:', filename);
    }

    // Dispatch completion event
    this.dispatchEvent(new CustomEvent('as-mesh-export-done', {
      bubbles: true,
      composed: true,
      detail: { filetype: fileType }
    }));
  }

  private getMeshURL(fileType: string, filename: string): Promise<string> {
    return new Promise((resolve, reject) => {
      window.dispatchEvent(new CustomEvent('get-mesh-url', {
        detail: {
          type: fileType,
          filename: filename,
          finalized: true,
          resolve,
          reject
        }
      }));
    });
  }

  private async fetchMeshBlob(url: string): Promise<Blob> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch mesh: ${response.statusText}`);
    }
    return await response.blob();
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('as-mesh-export-button')) {
  customElements.define('as-mesh-export-button', AsMeshExportButton);
} else {
  console.warn("as-mesh-export-button already defined");
}